import { useCallback, useRef, useState } from 'react';
import { MESSAGE_IMAGE_DRAG_MIME } from '@ruta/shared';

import { fetchImageAsFile, readMessageImageDrag } from '@/lib/dragImage';
import { convertHeicToJpegFile } from '@/lib/heic';
import { toast } from '@/components/ui/use-toast';

/**
 * Shared drag-and-drop + paste image attaching.
 *
 * Every place that accepts an image (customer/team photos, company logo, visit
 * photos, service attachments, service-template images, message composers, …)
 * already funnels manually-picked files through an `onFiles(files)` handler — the
 * same contract the shared `ImageUploadInput` uses. This hook lets that same
 * handler also receive dragged and pasted images: spread `dropZoneProps` and
 * `onPaste` onto the target element and drive a highlight with `isDragging`.
 *
 * Files are handed back already HEIC-converted, exactly like `ImageUploadInput`,
 * so each caller's validation/upload path stays identical regardless of source
 * (manual pick, OS file drop, screenshot paste, or an image dragged out of a
 * customer-message thread).
 */

/**
 * A drag may carry attachable image data (OS files or a message-thread image).
 * During dragover Safari/Chrome sometimes omit "Files" from `types` until drop,
 * so we also treat `items` with kind "file" as a hit — otherwise we never call
 * preventDefault and the browser opens the file in a new tab.
 */
function dragCarriesImage(dataTransfer: DataTransfer | null | undefined): boolean {
  if (!dataTransfer) return false;
  const types = Array.from(dataTransfer.types ?? []);
  if (types.includes('Files') || types.includes(MESSAGE_IMAGE_DRAG_MIME)) return true;
  // DOMStringList.has / contains on older browsers
  const list = dataTransfer.types as DataTransfer['types'] & { contains?: (t: string) => boolean };
  if (typeof list.contains === 'function' && list.contains('Files')) return true;
  if (dataTransfer.items && Array.from(dataTransfer.items).some((it) => it.kind === 'file')) {
    return true;
  }
  return false;
}

/** True for any OS-file (or message-image) drag — used only to claim the drop. */
function dragCarriesFiles(dataTransfer: DataTransfer | null | undefined): boolean {
  return dragCarriesImage(dataTransfer);
}

/**
 * Collect attachable Files from a drag/paste transfer. OS file drops come through
 * `files` directly; an image dragged out of a message thread arrives as a URL
 * descriptor we fetch into a File. Everything is HEIC-converted so callers get
 * the same normalized File[] the shared upload input hands back.
 */
async function filesFromTransfer(dataTransfer: DataTransfer | null): Promise<File[]> {
  const direct = Array.from(dataTransfer?.files ?? []);
  if (direct.length > 0) {
    // Keep images (and extension-less / HEIC that often arrive with empty type).
    const images = direct.filter(
      (f) =>
        f.type.startsWith('image/')
        || !f.type
        || /\.(heic|heif|jpe?g|png|gif|webp|tiff?|bmp)$/i.test(f.name),
    );
    return Promise.all(images.map(convertHeicToJpegFile));
  }
  const threadImage = readMessageImageDrag(dataTransfer);
  if (threadImage) return [await fetchImageAsFile(threadImage)];
  return [];
}

interface UseImageDropPasteOptions {
  /** Receives attachable files from a drop or paste — same shape as `ImageUploadInput`'s `onFiles`. */
  onFiles: (files: File[]) => void | Promise<void>;
  /** Skip handling while true (e.g. an upload is already in flight, or the surface is disabled). */
  disabled?: boolean;
}

export interface UseImageDropPasteResult {
  /** True while an attachable image is dragged over the zone — drive the highlight with this. */
  isDragging: boolean;
  /** Spread onto the drop-target element. */
  dropZoneProps: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  /** Bind to the element that should accept pasted screenshots (usually the same target). */
  onPaste: (e: React.ClipboardEvent) => void;
}

export function useImageDropPaste({
  onFiles,
  disabled = false,
}: UseImageDropPasteOptions): UseImageDropPasteResult {
  const [isDragging, setIsDragging] = useState(false);
  // Counter (not a boolean) so dragging across child elements doesn't flicker the
  // highlight: every enter increments, every leave decrements, clear only at zero.
  const dragDepth = useRef(0);

  const onDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (disabled || !dragCarriesFiles(e.dataTransfer)) return;
      // Claim the drop immediately so the browser never navigates to the file.
      e.preventDefault();
      // Stop bubbling so an inner drop zone (e.g. a dialog's box nested inside a
      // whole-panel drop zone) handles the drop alone — the outer zone must not
      // also fire, or the image would upload twice.
      e.stopPropagation();
      dragDepth.current += 1;
      setIsDragging(true);
    },
    [disabled],
  );

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled || !dragCarriesFiles(e.dataTransfer)) return;
      // preventDefault is required for the element to be a valid drop target;
      // without it the browser opens the file in a new tab on drop.
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    },
    [disabled],
  );

  const onDragLeave = useCallback((e: React.DragEvent) => {
    // Only count leaves that exit this zone (not enter a child).
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  }, []);

  const onDrop = useCallback(
    async (e: React.DragEvent) => {
      // Always prevent navigation when a file lands here, even if we reject it.
      e.preventDefault();
      e.stopPropagation();
      dragDepth.current = 0;
      setIsDragging(false);
      if (disabled) return;
      try {
        const files = await filesFromTransfer(e.dataTransfer);
        if (files.length === 0) {
          if (dragCarriesFiles(e.dataTransfer) || (e.dataTransfer?.files?.length ?? 0) > 0) {
            toast({
              title: 'Could not attach image',
              description: 'Drop a photo (JPEG, PNG, HEIC, …).',
              variant: 'destructive',
            });
          }
          return;
        }
        await onFiles(files);
      } catch (err) {
        console.error(err);
        toast({
          title: 'Could not attach image',
          description: err instanceof Error ? err.message : undefined,
          variant: 'destructive',
        });
      }
    },
    [disabled, onFiles],
  );

  // Paste a copied photo or a system-clipboard screenshot. Only acts when image
  // data is present, so pasting text into a nearby field is unaffected.
  const onPaste = useCallback(
    async (e: React.ClipboardEvent) => {
      if (disabled) return;
      const pasted = Array.from(e.clipboardData?.items ?? [])
        .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
        .map((it) => it.getAsFile())
        .filter((f): f is File => f !== null);
      if (pasted.length === 0) return;
      e.preventDefault();
      e.stopPropagation(); // inner zone handles it; don't also fire an outer zone
      try {
        await onFiles(await Promise.all(pasted.map(convertHeicToJpegFile)));
      } catch (err) {
        console.error(err);
        toast({
          title: 'Could not attach image',
          description: err instanceof Error ? err.message : undefined,
          variant: 'destructive',
        });
      }
    },
    [disabled, onFiles],
  );

  return {
    isDragging,
    dropZoneProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
    onPaste,
  };
}
